/**
 * Types the browser will put on screen for us, as they are.
 *
 * A smaller set than what may be UPLOADED, and no longer the whole story:
 * `ACCEPTED_DOCUMENT_TYPES` also allows `image/tiff` and `image/heic`, which
 * this app now decodes for itself rather than offering as downloads. See
 * `DECODABLE_TYPES` in `spotlight.decode.ts` — between the two lists, every
 * format a collector may upload is shown on the screen it was asked about.
 */
export const VIEWABLE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/** Types a browser renders in a frame rather than an image element. */
export const FRAMEABLE_TYPES = ["application/pdf"];

export const SPOTLIGHT_COPY = {
  close: "Close",
  /** Under every framed or undisplayable slide — some mobile browsers will not
   *  inline a PDF, and a frame that stays blank tells the reader nothing. */
  openInTab: "Open in a new tab",
  download: "Download",

  /**
   * Storage refused the bytes.
   *
   * The same argument `signWorkImage` makes about a photograph: being refused
   * is a state rather than a fault. A reader can reach an entry through a route
   * that does not reach the paperwork behind it.
   */
  refused:
    "This document could not be opened. The registry holds it, but these bytes are not shared with this account.",

  /** A type the browser cannot decode. Named, so it is fixable. */
  undisplayable: (type: string) =>
    `Browsers cannot display ${type} files. It can still be downloaded and opened.`,

  /** A type outside the accepted set — an older row, or one uploaded elsewhere. */
  unknown: (type: string) => `This is a ${type} file, which cannot be shown here.`,

  /**
   * Said while a TIFF or a HEIC is being turned into something showable.
   *
   * Named, rather than a bare spinner: the wait is proportional to the file and
   * a 40MB scan takes seconds, which reads as a hang unless the screen says
   * what it is doing.
   */
  converting: (type: string) =>
    `No browser draws ${type} files, so this one is being converted here. One moment.`,

  /**
   * The conversion itself failed — a truncated upload, an exotic TIFF variant,
   * a browser that would not give up a canvas. The bytes are still whole, so
   * the download link under it is the way out.
   */
  conversionFailed: (type: string) =>
    `This ${type} file could not be converted for display. It can still be downloaded and opened.`,

  /** No content type at all on the row. Said as ignorance, not as a fault. */
  untyped: "This document does not say what kind of file it is, so it is offered as a download.",
} as const;
